import { clamp } from "../utils/math";
import type { Rng } from "../utils/random";
import type { GenreFeatures, RhythmRole } from "./musicTypes";

/**
 * Turns "which steps sound" into "how they are played".
 *
 * This is the only place Energy is allowed to act. It shapes velocity, accent
 * depth and articulation — never whether a note exists. That is what keeps
 * "high energy / low density" audibly different from "high density": few
 * events, each of them emphatic.
 */

/** Spec limit: humanisation must never exceed this. */
export const MAX_TIMING_OFFSET_MS = 60;

export interface PerformanceInputs {
  role: RhythmRole;
  isAccent: boolean;
  isGhost: boolean;
  /** Step index within the bar. */
  step: number;
  stepsPerBar: number;
  energy: number;
  features: GenreFeatures;
  /** 0..1. Phase 1A uses a fixed default; the Music panel exposes it later. */
  humanization: number;
  rng: Rng;
}

export interface PerformanceResult {
  velocity: number;
  timingOffsetMs: number;
}

/**
 * Per-role timing character.
 *
 * A single global random offset would just smear everything. Real ensembles
 * differ by instrument: the kick holds the grid, hats lean forward, hand
 * percussion is loose. `mean` is the systematic lean, `spread` the variability.
 */
const TIMING_CHARACTER: Record<RhythmRole, { meanMs: number; spreadMs: number }> = {
  kick: { meanMs: 0, spreadMs: 2.5 },
  pulse: { meanMs: -3.5, spreadMs: 4 },
  percussion: { meanMs: 2.5, spreadMs: 8 },
  accent: { meanMs: 1.5, spreadMs: 5 },
};

const BASE_VELOCITY: Record<RhythmRole, number> = {
  kick: 0.62,
  pulse: 0.34,
  percussion: 0.32,
  accent: 0.45,
};

export function performEvent(inputs: PerformanceInputs): PerformanceResult {
  const { role, isAccent, isGhost, energy, features, humanization, rng } = inputs;

  // --- velocity: Energy's main lever -------------------------------------
  const base = BASE_VELOCITY[role];
  // Energy raises the floor and, more importantly, widens the accent range:
  // high energy is as much about contrast as about loudness.
  let velocity = base * (0.62 + energy * 0.62);

  if (isAccent) {
    velocity += 0.12 + energy * 0.38;
  }
  if (isGhost) {
    velocity *= 0.42 - energy * 0.06;
  }

  // The kick is where Energy is most audible.
  if (role === "kick") {
    velocity += energy * 0.16;
  }

  // Small per-note variation so repeated hits are not identical.
  velocity += rng.gaussian() * 0.03 * humanization;
  velocity = clamp(velocity, 0.05, 1);

  // --- timing: humanisation ----------------------------------------------
  const character = TIMING_CHARACTER[role];
  let offset = character.meanMs + rng.gaussian() * character.spreadMs * (0.35 + humanization);

  // Jazz sits back, and lays back hardest on the weak positions.
  const stepsPerBeat = inputs.stepsPerBar / 4;
  const offBeat = inputs.step % stepsPerBeat !== 0;
  if (offBeat || isGhost) {
    offset += features.laybackAmount * 22;
  }

  // A tight grid pulls everything back towards zero.
  offset *= 1 - features.rhythmicRegularity * 0.45;

  return {
    velocity,
    timingOffsetMs: clamp(offset, -MAX_TIMING_OFFSET_MS, MAX_TIMING_OFFSET_MS),
  };
}

/**
 * Whether this bar should be a fill.
 *
 * Energy gates the *probability*, but a fill only reshapes the last beat
 * (see RhythmEngine.applyFill), so this cannot change the bar's event count.
 * The cooldown is what stops the "fill every four bars" cliché.
 */
export function shouldFill(
  barsSinceFill: number,
  isSectionEnd: boolean,
  energy: number,
  features: GenreFeatures,
  rng: Rng,
): boolean {
  if (barsSinceFill < 3) return false;
  const base = isSectionEnd ? 0.55 : 0.06;
  const probability = base + energy * 0.22 + features.patternMutation * 0.12;
  return rng.chance(clamp(probability, 0, 0.9));
}
